import {
  Alert,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  FormControl,
  InputLabel,
} from "@mui/material";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthService } from "../services/auth-service";
import { useNavigate, Link } from "react-router-dom";
import Logo from "../assets/img/logos/divixBlanco.png";
import { AcceptTermsModal } from "../components/modals/AcceptTermsModal";
import { LocationService } from "../services/location-service";
import { Country } from "../services/dto/response/location-response-dto";
import Turnstile from "react-turnstile";

// Países que no requieren provincia/ciudad (por isoCode)
const COUNTRIES_WITHOUT_REGIONS = ["DO"]; // República Dominicana

const isCountryWithoutRegions = (isoCode: string) => {
  return COUNTRIES_WITHOUT_REGIONS.includes(isoCode);
};

export function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locationService = useMemo(() => new LocationService(), []);

  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    nameCompany: "",
    address: "",
    cedula: "",
    countryId: "",
  });

  const [countries, setCountries] = useState<Country[]>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(true);

  const [openNotification, setOpenNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState<"success" | "error">("error");
  const [loading, setLoading] = useState(false);
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState<string>("");

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // País seleccionado
  const selectedCountry = useMemo(() => {
    return countries.find((c) => c.id.toString() === formData.countryId);
  }, [countries, formData.countryId]);

  // Cargar países al montar
  useEffect(() => {
    setIsLoadingCountries(true);
    locationService.getCountries(
      (data) => {
        setCountries(data);
        setIsLoadingCountries(false);
      },
      () => {
        setIsLoadingCountries(false);
      }
    );
  }, [locationService]);

  // Obtener la clave del sitio de Cloudflare Turnstile desde las variables de entorno
  useEffect(() => {
    const siteKey = process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY || "";
    setTurnstileKey(siteKey);
    if (!siteKey) {
      console.warn("⚠️ REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY no está configurada. El captcha no se mostrará.");
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    let isValid = true;

    if (!formData.countryId) {
      newErrors.countryId = t("register.errors.countryId");
      isValid = false;
    }

    if (!formData.firstName?.trim()) {
      newErrors.firstName = t("register.errors.firstName");
      isValid = false;
    } else if (formData.firstName.length < 3) {
      newErrors.firstName = t("register.errors.firstName-min");
      isValid = false;
    }

    if (!formData.lastName?.trim()) {
      newErrors.lastName = t("register.errors.lastName");
      isValid = false;
    } else if (formData.lastName.length < 3) {
      newErrors.lastName = t("register.errors.lastName-min");
      isValid = false;
    }

    if (!formData.email) {
      newErrors.email = t("register.errors.email");
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("register.errors.email-invalid");
      isValid = false;
    }

    if (!formData.nameCompany?.trim()) {
      newErrors.nameCompany = t("register.errors.nameCompany");
      isValid = false;
    }

    if (!formData.phone) {
      newErrors.phone = t("register.errors.phone-required");
      isValid = false;
    } else if (formData.phone.length < 7 || formData.phone.length > 20) {
      newErrors.phone = t("register.errors.phone-size");
      isValid = false;
    }

    if (!formData.address?.trim()) {
      newErrors.address = t("register.errors.address");
      isValid = false;
    }

    if (!formData.cedula) {
      newErrors.cedula = t("register.errors.cedula");
      isValid = false;
    } else {
      // Validar según el país seleccionado
      const isDominicanRepublic = selectedCountry?.isoCode === "DO";
      if (isDominicanRepublic) {
        // Para República Dominicana: debe tener entre 8 y 11 dígitos
        if (formData.cedula.length < 8 || formData.cedula.length > 11) {
          newErrors.cedula = t("register.errors.cedula-length");
          isValid = false;
        }
      } else {
        // Para otros países: mantener validación de 11 dígitos (o ajustar según necesidad)
        if (formData.cedula.length !== 11) {
          newErrors.cedula = t("register.errors.cedula-length");
          isValid = false;
        }
      }
    }

    if (!isTermsChecked) {
      newErrors.terms = t("register.errors.terms");
      isValid = false;
    }

    // Solo validar el captcha si hay una key configurada
    if (turnstileKey && !turnstileToken) {
      newErrors.captcha = "Por favor, completa la verificación de seguridad";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleNotificationClose = () => {
    setOpenNotification(false);
  };

  const handleAcceptTerms = () => {
    setIsTermsChecked(true);
    setIsOpenModal(false);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (validate() && selectedCountry) {
      const authService = new AuthService();
      await authService.registerManager(
        {
          email: formData.email.toLowerCase(),
          name: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          nameCompany: formData.nameCompany,
          address: formData.address,
          cuitCuil: formData.cedula,
          countryId: selectedCountry.id.toString(),
          countryCode: selectedCountry.isoCode,
          turnstileToken: turnstileToken || undefined,
        },
        () => {
          setNotificationType("success");
          setNotificationMessage(t("register.success"));
          setOpenNotification(true);
          setTimeout(() => {
            navigate("/login");
          }, 5000);
        },
        (errorMessage) => {
          setNotificationType("error");
          setNotificationMessage(errorMessage || t("register.error"));
          setOpenNotification(true);
        }
      );
    }

    setLoading(false);
  };

  const textFieldSx = {
    "& .MuiInput-root": {
      color: "#fff",
      fontFamily: "Montserrat",
      "&:before": {
        borderBottom: "1px solid #838383",
      },
      "&:hover:not(.Mui-disabled):before": {
        borderBottomColor: "#898989",
      },
    },
    "& label.Mui-focused": {
      color: "#a9a9a9",
    },
    "& .MuiInputLabel-standard": {
      color: "#a9a9a9",
      fontWeight: "300",
      fontFamily: "Montserrat",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#838383",
    },
    "& .MuiFormHelperText-root": {
      color: "#d32f2f",
      marginLeft: 0,
      marginTop: "3px",
      fontSize: "0.75rem",
    },
  };

  const selectSx = {
    "& .MuiSelect-select": {
      color: "#fff",
      fontFamily: "Montserrat",
    },
    "& .MuiInput-root": {
      color: "#fff",
      "&:before": {
        borderBottom: "1px solid #838383",
      },
      "&:hover:not(.Mui-disabled):before": {
        borderBottomColor: "#898989",
      },
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#838383",
    },
    "& .MuiSvgIcon-root": {
      color: "#838383",
    },
  };

  return (
    <div className="container min-h-screen relative grid flex-col items-center justify-center lg:max-w-none lg:grid-cols-2 lg:px-0">
      {/* Logo section */}
      <div className="relative hidden h-full flex-col p-10 text-white lg:flex lg:justify-center lg:items-center bg-dark-gradient-to-b">
        <img className="w-32 mx-auto" src={Logo} alt="Divix Logo" />
        <h1 className="mt-6 text-3xl font-light tracking-wide">
          {t("register.welcome")}
        </h1>
        <p className="mt-2 text-lg font-semibold tracking-widest">
          {t("register.subtitle")}
        </p>
      </div>

      {/* Register section */}
      <div className="lg:p-4 py-8 overflow-y-auto max-h-screen">
        <div className="mx-auto text-white flex w-full border rounded-xl flex-col justify-center space-y-4 py-6 sm:w-[450px] bg-dark-gradient-to-b">
          <div className="flex space-y-2 mx-auto">
            <svg
              width="60"
              height="60"
              viewBox="-1.68 -1.68 27.36 27.36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              stroke="#EF0BB8"
            >
              <g strokeLinecap="round" strokeLinejoin="round" stroke="#EF0BB8" strokeWidth=".312">
                <circle opacity=".5" cx="12" cy="9" r="3" />
                <circle cx="12" cy="12" r="10" />
                <path opacity=".5" d="M17.97 20c-.16-2.892-1.045-5-5.97-5s-5.81 2.108-5.97 5" />
              </g>
            </svg>
          </div>

          <h2 className="text-center text-xl font-light">{t("register.title")}</h2>

          <form onSubmit={handleSubmit} className="px-6">
            <div className="grid gap-3">
              {/* País y Nombre */}
              <div className="grid grid-cols-2 gap-3">
                <FormControl variant="standard" error={!!errors.countryId}>
                  <InputLabel sx={{ color: "#a9a9a9", fontFamily: "Montserrat", fontWeight: "300" }}>
                    {t("register.form.country")}
                  </InputLabel>
                  <Select
                    value={formData.countryId}
                    onChange={(e) => handleSelectChange("countryId", e.target.value)}
                    disabled={isLoadingCountries}
                    sx={selectSx}
                    MenuProps={{
                      PaperProps: {
                        sx: { maxHeight: 200 }
                      }
                    }}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.id} value={country.id.toString()}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.countryId && (
                    <span className="text-[#d32f2f] text-xs mt-1">{errors.countryId}</span>
                  )}
                </FormControl>

                <TextField
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  onChange={handleInputChange}
                  onInput={(e) => {
                    const input = e.target as HTMLInputElement;
                    input.value = input.value.replace(/[^a-zA-ZÀ-ÿ\s']/g, "");
                  }}
                  label={t("register.form.firstName")}
                  variant="standard"
                  sx={textFieldSx}
                />
              </div>

              {/* Apellido y Email */}
              <div className="grid grid-cols-2 gap-3">
                <TextField
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  onChange={handleInputChange}
                  onInput={(e) => {
                    const input = e.target as HTMLInputElement;
                    input.value = input.value.replace(/[^a-zA-ZÀ-ÿ\s']/g, "");
                  }}
                  label={t("register.form.lastName")}
                  variant="standard"
                  sx={textFieldSx}
                />

                <TextField
                  id="email"
                  name="email"
                  autoComplete="email"
                  type="email"
                  value={formData.email}
                  error={!!errors.email}
                  helperText={errors.email}
                  onChange={handleInputChange}
                  label={t("register.form.email")}
                  variant="standard"
                  sx={textFieldSx}
                />
              </div>

              {/* Nombre empresa y Teléfono */}
              <div className="grid grid-cols-2 gap-3">
                <TextField
                  id="nameCompany"
                  name="nameCompany"
                  type="text"
                  value={formData.nameCompany}
                  error={!!errors.nameCompany}
                  helperText={errors.nameCompany}
                  onChange={handleInputChange}
                  label={t("register.form.nameCompany")}
                  variant="standard"
                  sx={textFieldSx}
                />

                <TextField
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  onChange={handleInputChange}
                  onInput={(e) => {
                    const input = e.target as HTMLInputElement;
                    let value = input.value.replace(/[^\d\s+]/g, "");
                    value = value.replace(/\s+/g, " ");
                    value = value.replace(/(?!^)\+/g, "");
                    if (value.includes("+") && value[0] !== "+") {
                      value = "+" + value.replace(/\+/g, "");
                    }
                    input.value = value;
                  }}
                  label={t("register.form.phone")}
                  variant="standard"
                  sx={textFieldSx}
                />
              </div>

              {/* Dirección */}
              <TextField
                id="address"
                name="address"
                type="text"
                value={formData.address}
                error={!!errors.address}
                helperText={errors.address}
                onChange={handleInputChange}
                label={t("register.form.address")}
                variant="standard"
                sx={textFieldSx}
              />

              {/* Cédula */}
              <TextField
                id="cedula"
                name="cedula"
                type="text"
                value={formData.cedula}
                error={!!errors.cedula}
                helperText={errors.cedula}
                onChange={handleInputChange}
                onInput={(e) => {
                  const input = e.target as HTMLInputElement;
                  input.value = input.value.replace(/[^\d]/g, "").slice(0, 11);
                }}
                label={t("register.form.cedula")}
                variant="standard"
                sx={textFieldSx}
              />
            </div>

            <div className="w-full text-[#898989] italic text-xs px-2 py-4 flex items-center justify-center">
              <FormControlLabel
                sx={{
                  "& .MuiTypography-root": {
                    fontSize: "0.75rem",
                    color: "#898989",
                    lineHeight: "1rem",
                    fontStyle: "italic",
                    paddingLeft: "2px",
                    fontFamily: "Montserrat",
                  },
                }}
                control={
                  <Checkbox
                    checked={isTermsChecked}
                    onChange={() => setIsTermsChecked(!isTermsChecked)}
                    sx={{
                      color: "#898989",
                      padding: 0,
                      "&.Mui-checked": {
                        color: "#EF0BB8",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: 16,
                        margin: 0,
                        padding: 0,
                      },
                    }}
                  />
                }
                label={
                  <span>
                    {t("register.form.accept")}{" "}
                    <span
                      className="hover:underline cursor-pointer text-[#EF0BB8]"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsOpenModal(true);
                      }}
                    >
                      {t("register.form.terms")}
                    </span>
                  </span>
                }
              />
            </div>
            {errors.terms && (
              <p className="text-red-500 text-xs text-center mb-2">{errors.terms}</p>
            )}

            {/* Cloudflare Turnstile Captcha */}
            {turnstileKey && (
              <div className="flex justify-center px-6 py-2">
                <Turnstile
                  sitekey={turnstileKey}
                  theme="dark"
                  onVerify={(token: string) => {
                    setTurnstileToken(token);
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.captcha;
                      return newErrors;
                    });
                  }}
                  onError={() => {
                    setTurnstileToken(null);
                    setErrors((prev) => ({ ...prev, captcha: "Error en la verificación. Por favor, intenta nuevamente." }));
                  }}
                  onExpire={() => {
                    setTurnstileToken(null);
                  }}
                />
                {errors.captcha && (
                  <div className="text-red-500 text-xs mt-1 text-center w-full">
                    {errors.captcha}
                  </div>
                )}
              </div>
            )}

            <div className="w-3/4 mx-auto mt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full transition-colors leading-5 bg-[#4f4f4f] hover:bg-opacity-90 text-white font-light rounded-3xl py-1 px-4"
              >
                {!loading ? (
                  t("register.form.submit")
                ) : (
                  <CircularProgress
                    sx={{ color: "#fff" }}
                    size="1rem"
                    className="animate-spin"
                  />
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="mx-auto mt-4 sm:w-[450px] text-center">
          <span className="text-black">
            {t("register.hasAccount")}{" "}
            <Link to="/login" className="font-bold hover:underline">
              {t("register.login")}
            </Link>
          </span>
        </div>
      </div>

      {openNotification && (
        <Snackbar
          open={openNotification}
          autoHideDuration={6000}
          onClose={handleNotificationClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert onClose={handleNotificationClose} severity={notificationType}>
            {notificationMessage}
          </Alert>
        </Snackbar>
      )}

      {isOpenModal && (
        <AcceptTermsModal
          onAccept={handleAcceptTerms}
          isOpenModal={isOpenModal}
          setIsOpenModal={setIsOpenModal}
        />
      )}
    </div>
  );
}
